// SeasonPlanReview — displayed after AI Coach generates the plan, before committing to calendar.
// Shows full structured plan: summary, training blocks, week 0 session preview, then confirms.

import { useState } from 'react';
import { CalendarCheck, MessageCircle, Loader2, Flag, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CoachChatResponse } from '@/lib/api';
import { RunnerProcessingIndicator } from '@/components/coach/RunnerProcessingIndicator';

const TEAL = '0, 255, 200';
const AMBER = '255, 180, 60';
const GREY_TEXT = '#A0A0A0';
const WHITE_TEXT = '#E8E8E8';
const CARD_BG = 'rgba(12, 16, 24, 0.9)';
const BORDER_INACTIVE = 'rgba(255, 255, 255, 0.12)';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function sessionTypeStyle(workoutType: string): React.CSSProperties {
  const t = workoutType.toLowerCase();
  if (t.includes('long'))
    return { color: `rgba(${TEAL}, 0.85)` };
  if (t.includes('tempo') || t.includes('threshold'))
    return { color: `rgba(${AMBER}, 0.85)` };
  if (t.includes('interval') || t.includes('speed') || t.includes('vo2'))
    return { color: 'rgba(240, 130, 130, 0.85)' };
  if (t.includes('rest') || t.includes('recovery'))
    return { color: 'rgba(100,116,139,0.55)' };
  return { color: GREY_TEXT };
}

function weekdayFromISO(iso: string): string {
  try {
    const d = new Date(iso + 'T00:00:00');
    return DAY_NAMES[d.getDay() === 0 ? 6 : d.getDay() - 1] ?? iso.slice(0, 10);
  } catch {
    return iso.slice(0, 10);
  }
}

function computeWeeks(startDate: string, endDate: string): number {
  try {
    const diff = new Date(endDate).getTime() - new Date(startDate).getTime();
    return Math.max(1, Math.ceil(diff / (7 * 24 * 60 * 60 * 1000)));
  } catch {
    return 0;
  }
}

interface SeasonPlanReviewProps {
  response: CoachChatResponse;
  onConfirm: () => Promise<void>;
  onAdjust: () => void;
}

export function SeasonPlanReview({
  response,
  onConfirm,
  onAdjust,
}: SeasonPlanReviewProps) {
  const [isConfirming, setIsConfirming] = useState(false);

  const sp = response.season_plan;
  const season = sp?.season;
  const week0 = sp?.week0;
  const days0 = sp?.days0 ?? [];
  const calSessions = sp?.calendar_sessions ?? [];
  const athleteSummary = sp?.athlete_summary;

  const nWeeks = season ? computeWeeks(season.start_date, season.end_date) : null;
  const race = season?.races?.[0];
  const phases = season?.phases ?? [];

  const replyText = response.reply ?? '';

  const glassCard: React.CSSProperties = {
    background: CARD_BG,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: `1px solid ${BORDER_INACTIVE}`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      setIsConfirming(false);
    }
  };

  // Sessions to display: prefer calendar_sessions (have real dates), fall back to days0
  const sessionRows = calSessions.length > 0
    ? calSessions.map((s, i) => ({
        label: weekdayFromISO(s.date),
        type: s.workout_type,
        detail: s.duration_or_structure,
        index: i,
      }))
    : days0.map((d, i) => ({
        label: DAY_NAMES[i] ?? `Day ${i + 1}`,
        type: d.workout_type,
        detail: d.duration_or_structure,
        index: i,
      }));

  if (isConfirming) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <RunnerProcessingIndicator
          caption="Applying plan to your calendar…"
          speedMultiplier={1.2}
        />
        <p
          className="font-mono text-[10px] uppercase tracking-wider"
          style={{ color: GREY_TEXT }}
        >
          Creating sessions
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-xl font-semibold" style={{ color: WHITE_TEXT }}>
            Your Plan
          </h2>
          <span
            className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded"
            style={{
              background: `rgba(${TEAL}, 0.08)`,
              border: `1px solid rgba(${TEAL}, 0.2)`,
              color: `rgba(${TEAL}, 0.7)`,
            }}
          >
            Ready to Review
          </span>
        </div>
        {athleteSummary && (
          <p className="font-mono text-[10px]" style={{ color: GREY_TEXT }}>
            Based on: {athleteSummary}
          </p>
        )}
      </div>

      {/* Season summary */}
      {season && (
        <div
          className="p-4 rounded-lg space-y-2"
          style={{ ...glassCard, border: `1px solid rgba(${TEAL}, 0.14)` }}
        >
          <div className="flex items-center gap-3 flex-wrap">
            {nWeeks !== null && (
              <span
                className="font-mono text-2xl font-semibold tabular-nums"
                style={{ color: `rgba(${TEAL}, 0.9)` }}
              >
                {nWeeks}
                <span
                  className="font-mono text-[10px] uppercase tracking-wider ml-1"
                  style={{ color: GREY_TEXT }}
                >
                  wks
                </span>
              </span>
            )}
            {race && (
              <div className="flex items-center gap-1.5">
                <Flag
                  className="h-3 w-3 flex-shrink-0"
                  style={{ color: `rgba(${AMBER}, 0.7)` }}
                />
                <span
                  className="font-mono text-xs font-medium"
                  style={{ color: `rgba(${AMBER}, 0.9)` }}
                >
                  {race.distance_or_type}
                </span>
                <span className="font-mono text-[10px]" style={{ color: GREY_TEXT }}>
                  · {race.date}
                </span>
              </div>
            )}
          </div>
          <p className="font-mono text-[10px]" style={{ color: GREY_TEXT }}>
            {season.start_date} → {season.end_date}
          </p>
          {season.training_philosophy && (
            <p className="font-mono text-[10px] italic" style={{ color: GREY_TEXT }}>
              {season.training_philosophy}
            </p>
          )}
        </div>
      )}

      {/* Training blocks */}
      {phases.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Layers className="h-3 w-3" style={{ color: GREY_TEXT }} />
            <p
              className="font-mono text-[9px] uppercase tracking-wider"
              style={{ color: GREY_TEXT }}
            >
              Training Blocks · {phases.length}
            </p>
          </div>
          <div className="space-y-1.5">
            {phases.map((phase, i) => (
              <div
                key={i}
                className="flex items-start gap-3 px-3 py-2.5 rounded-lg"
                style={glassCard}
              >
                <div
                  className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                  style={{
                    background: `rgba(${TEAL}, 0.1)`,
                    border: `1px solid rgba(${TEAL}, 0.25)`,
                  }}
                >
                  <span
                    className="font-mono text-[8px] font-bold"
                    style={{ color: `rgba(${TEAL}, 0.8)` }}
                  >
                    {i + 1}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="font-mono text-[11px] font-medium uppercase tracking-wider"
                    style={{ color: WHITE_TEXT }}
                  >
                    {phase.phase_name}
                  </p>
                  <p
                    className="font-mono text-[10px] mt-0.5 leading-relaxed"
                    style={{ color: GREY_TEXT }}
                  >
                    {phase.goal_description}
                  </p>
                  {phase.volume_rule && (
                    <p
                      className="font-mono text-[9px] mt-0.5 italic"
                      style={{ color: 'rgba(160,160,160,0.45)' }}
                    >
                      {phase.volume_rule}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Week 1 session preview */}
      {sessionRows.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <p
              className="font-mono text-[9px] uppercase tracking-wider"
              style={{ color: GREY_TEXT }}
            >
              Week 1 Preview
            </p>
            {week0 && (
              <span
                className="font-mono text-[9px] px-1.5 py-0.5 rounded"
                style={{
                  background: `rgba(${TEAL}, 0.06)`,
                  border: `1px solid rgba(${TEAL}, 0.15)`,
                  color: `rgba(${TEAL}, 0.6)`,
                }}
              >
                {week0.phase_name} · {week0.target_volume}h
              </span>
            )}
          </div>
          <div className="space-y-0.5">
            {sessionRows.map((row) => (
              <div
                key={row.index}
                className="flex items-baseline gap-2 px-2.5 py-1.5 rounded"
                style={{
                  background: 'rgba(255,255,255,0.015)',
                  border: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <span
                  className="font-mono text-[9px] uppercase tracking-wider w-7 flex-shrink-0"
                  style={{ color: 'rgba(160,160,160,0.45)' }}
                >
                  {row.label}
                </span>
                <span
                  className="font-mono text-[11px] font-medium w-20 flex-shrink-0 capitalize"
                  style={sessionTypeStyle(row.type)}
                >
                  {row.type}
                </span>
                {row.detail && (
                  <span
                    className="font-mono text-[10px] flex-1 truncate"
                    style={{ color: GREY_TEXT }}
                  >
                    {row.detail}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fallback: plain coach reply when no structured plan data */}
      {!season && replyText && (
        <div
          className="p-4 rounded-lg"
          style={{ ...glassCard, border: `1px solid rgba(${TEAL}, 0.12)` }}
        >
          <p
            className="font-mono text-[9px] uppercase tracking-widest mb-2"
            style={{ color: `rgba(${TEAL}, 0.55)` }}
          >
            Coach
          </p>
          <p
            className="font-mono text-sm leading-relaxed whitespace-pre-line"
            style={{ color: GREY_TEXT }}
          >
            {replyText}
          </p>
        </div>
      )}

      {/* CTAs */}
      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onAdjust}
          disabled={isConfirming}
          className="flex-1 font-mono text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
        >
          <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
          Adjust
        </Button>

        <Button
          size="sm"
          onClick={handleConfirm}
          disabled={isConfirming}
          className="flex-1 font-mono text-xs"
          style={{
            background: `rgba(${TEAL}, 0.12)`,
            border: `1px solid rgba(${TEAL}, 0.3)`,
            color: `rgba(${TEAL}, 0.95)`,
          }}
        >
          {isConfirming ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Applying…
            </>
          ) : (
            <>
              <CalendarCheck className="h-3.5 w-3.5 mr-1.5" />
              Add to Calendar
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
